import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { invoiceService } from "../services/invoice.service";

export const createInvoice = createAsyncThunk("createInvoice", async (data, { rejectWithValue }) => {
    try {
        return await invoiceService.createInvoice(data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const getInvoiceList = createAsyncThunk("getInvoiceList", async (params, { rejectWithValue }) => {
    try {
        return await invoiceService.getInvoiceList(params);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const getPIList = createAsyncThunk("getPIList", async (params, { rejectWithValue }) => {
    try {
        return await invoiceService.getPIList(params);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const getInvoice = createAsyncThunk("getInvoice", async (id, { rejectWithValue }) => {
    try {
        return await invoiceService.getInvoice(id);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const updateInvoice = createAsyncThunk("updateInvoice", async ({data, id}, { rejectWithValue }) => {
    try {
        return await invoiceService.updateInvoice(id, data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const deleteInvoice = createAsyncThunk("deleteInvoice", async (id, { rejectWithValue }) => {
    try {
        return await invoiceService.deleteInvoice(id);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

const invoiceSlice = createSlice({
    name: "pmo-invoice",
    initialState: {
        list: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        detail: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        create: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        update: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        delete: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        piList: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
    },
    reducers: {
        resetStateInvoice: (state) => {
            state.list.isError = false;
            state.list.message = null;
            state.detail.isError = false;
            state.detail.message = null;
            state.create.isError = false;
            state.create.message = null;
            state.create.data = null;
            state.update.isError = false;
            state.update.message = null;
            state.update.data = null;
            state.delete.isError = false;
            state.delete.message = null;
            state.delete.data = null;
            state.piList.isError = false;
            state.piList.message = null;
            state.piList.data = null;
        },
    },
    extraReducers: (builder) => {
        // get invoice list
        builder.addCase(getInvoiceList.pending, (state) => {
            state.list.isLoading = true;
        });
        builder.addCase(getInvoiceList.fulfilled, (state, action) => {
            state.list.isLoading = false;
            state.list.data = action.payload;
            state.list.isError = false;
        });
        builder.addCase(getInvoiceList.rejected, (state, action) => {
            state.list.isLoading = false;
            state.list.isError = true;
            state.list.message = action.payload?.message;
        });

        // get invoice detail
        builder.addCase(getInvoice.pending, (state) => {
            state.detail.isLoading = true;
        });
        builder.addCase(getInvoice.fulfilled, (state, action) => {
            state.detail.isLoading = false;
            state.detail.data = action.payload;
            state.detail.isError = false;
        });
        builder.addCase(getInvoice.rejected, (state, action) => {
            state.detail.isLoading = false;
            state.detail.isError = true;
            state.detail.message = action.payload?.message;
        });

        // create invoice
        builder.addCase(createInvoice.pending, (state) => {
            state.create.isLoading = true;
        });
        builder.addCase(createInvoice.fulfilled, (state, action) => {
            state.create.isLoading = false;
            state.create.data = action.payload;
            state.create.isError = false;
        });
        builder.addCase(createInvoice.rejected, (state, action) => {
            state.create.isLoading = false;
            state.create.isError = true;
            state.create.message = action.payload?.message;
        });

        // update invoice
        builder.addCase(updateInvoice.pending, (state) => {
            state.update.isLoading = true;
        });
        builder.addCase(updateInvoice.fulfilled, (state, action) => {
            state.update.isLoading = false;
            state.update.data = action.payload;
            state.update.isError = false;
        });
        builder.addCase(updateInvoice.rejected, (state, action) => {
            state.update.isLoading = false;
            state.update.isError = true;
            state.update.message = action.payload?.message;
        });

        // delete invoice
        builder.addCase(deleteInvoice.pending, (state) => {
            state.delete.isLoading = true;
        });
        builder.addCase(deleteInvoice.fulfilled, (state, action) => {
            state.delete.isLoading = false;
            state.delete.data = action.payload;
            state.delete.isError = false;
        });
        builder.addCase(deleteInvoice.rejected, (state, action) => {
            state.delete.isLoading = false;
            state.delete.isError = true;
            state.delete.message = action.payload?.message;
        });

        // get PI list
        builder.addCase(getPIList.pending, (state) => {
            state.piList.isLoading = true;
        });
        builder.addCase(getPIList.fulfilled, (state, action) => {
            state.piList.isLoading = false;
            state.piList.data = action.payload;
            state.piList.isError = false;
        });
        builder.addCase(getPIList.rejected, (state, action) => {
            state.piList.isLoading = false;
            state.piList.isError = true;
            state.piList.message = action.payload?.message;
        });
    },
});

export default invoiceSlice.reducer;
export const { resetStateInvoice } = invoiceSlice.actions;

